import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import HomeOne from '../Pages/HomeOne';
import About from '../Pages/About';
import LoadTop from '../Components/LoadTop';
import Destination from '../Pages/Destination';
import DestinationDetails from '../Pages/DestinationDetails';
import Service from '../Pages/Service';
import ServiceDetails from '../Pages/ServiceDetails';
import Activities from '../Pages/Activities';
import ActivitiesDetails from '../Pages/ActivitiesDetails';
import Shop from '../Pages/Shop';
import ShopDetails from '../Pages/ShopDetails';
import Cart from '../Pages/Cart';
import Checkout from '../Pages/Checkout';
import Wishlist from '../Pages/Wishlist';
import Gallery from '../Pages/Gallery';
import Tour from '../Pages/Tour';
import TourDetails from '../Pages/TourDetails';
import Resort from '../Pages/Resort';
import ResortDetails from '../Pages/ResortDetails';
import TourGuide from '../Pages/TourGuide';
import TourGuiderDetails from '../Pages/TourGuiderDetails';
import Faq from '../Pages/Faq';
import Error from '../Pages/Error';
import ErrorWrapper from './ErrorWrapper';
import Blog from '../Pages/Blog';
import BlogDetails from '../Pages/BlogDetails';
import Contact from '../Pages/Contact';
import WhatsAppButton from '../Components/WhatsAppButton/WhatsAppButton';

function LanguageRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [isRouting, setIsRouting] = useState(false);
  const navigatingRef = useRef(false);
  const previousPathRef = useRef(location.pathname);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Handle pending navigation in useEffect (after render)
  useEffect(() => {
    if (pendingNavigation) {
      const { path, lang } = pendingNavigation;
      setPendingNavigation(null);
      navigatingRef.current = true;
      setIsRouting(true);
      
      if (lang && lang !== i18n.language) {
        i18n.changeLanguage(lang).then(() => {
          document.documentElement.lang = lang;
          navigate(path, { replace: true });
        }).catch((error) => {
          console.error('[LanguageRouter] Error changing language:', error);
          setIsRouting(false);
          navigatingRef.current = false;
        });
      } else {
        document.documentElement.lang = lang || i18n.language;
        navigate(path, { replace: true });
      }
    }
  }, [pendingNavigation, navigate, i18n]);

  useEffect(() => {
    // Reset navigating flag only when pathname actually changes (navigation completed)
    if (location.pathname !== previousPathRef.current) {
      if (navigatingRef.current) {
        navigatingRef.current = false;
        setIsRouting(false);
      }
      previousPathRef.current = location.pathname;
    }
  }, [location.pathname, i18n.language, i18n.resolvedLanguage]);

  // Check if a path is a valid route
  const isValidRoute = (pathToCheck) => {
    const validRoutes = [
      '/tr', '/tr/', '/en', '/en/',
      '/tr/home-tour', '/en/home-tour',
      '/tr/home-agency', '/en/home-agency',
      '/tr/home-yacht', '/en/home-yacht',
      '/tr/about', '/en/about',
      '/tr/destination', '/en/destination',
      '/tr/service', '/en/service',
      '/tr/activities', '/en/activities',
      '/tr/activities-details', '/en/activities-details',
      '/tr/shop', '/en/shop',
      '/tr/cart', '/en/cart',
      '/tr/checkout', '/en/checkout',
      '/tr/wishlist', '/en/wishlist',
      '/tr/gallery', '/en/gallery',
      '/tr/tour', '/en/tour',
      '/tr/tour-details', '/en/tour-details',
      '/tr/resort', '/en/resort',
      '/tr/tour-guide', '/en/tour-guide',
      '/tr/faq', '/en/faq',
      '/tr/error', '/en/error',
      '/tr/blog', '/en/blog',
      '/tr/contact', '/en/contact'
    ];
    
    // Check exact match
    if (validRoutes.includes(pathToCheck)) {
      return true;
    }
    
    // Check dynamic routes (with :id)
    const dynamicRoutes = [
      /^\/tr\/destination\/.+$/,
      /^\/en\/destination\/.+$/,
      /^\/tr\/service\/.+$/,
      /^\/en\/service\/.+$/,
      /^\/tr\/shop\/.+$/,
      /^\/en\/shop\/.+$/,
      /^\/tr\/resort\/.+$/,
      /^\/en\/resort\/.+$/,
      /^\/tr\/tour-guide\/.+$/,
      /^\/en\/tour-guide\/.+$/,
      /^\/tr\/blog\/.+$/,
      /^\/en\/blog\/.+$/
    ];
    
    return dynamicRoutes.some(pattern => pattern.test(pathToCheck));
  };

  // Use useLayoutEffect to catch route changes BEFORE React renders
  // This prevents the 404 page from flashing
  useLayoutEffect(() => {
    // If we're already navigating, skip
    if (navigatingRef.current) {
      return;
    }
    
    let path = location.pathname;
    
    // Normalize trailing slash: remove trailing slash except for root
    if (path !== '/' && path.endsWith('/')) {
      const normalizedPath = path.slice(0, -1);
      if (normalizedPath !== location.pathname) {
        setPendingNavigation({ path: normalizedPath, lang: null });
        return;
      }
    }
    
    const pathSegments = path.split('/').filter(Boolean);
    const firstSegment = pathSegments[0];
    const currentLang = i18n.resolvedLanguage || i18n.language || 'tr';

    // If first segment is a language code (tr or en) - valid path
    if (firstSegment === 'tr' || firstSegment === 'en') {
      const lang = firstSegment;
      
      if (currentLang !== lang) {
        setIsRouting(true);
        i18n.changeLanguage(lang).then(() => {
          document.documentElement.lang = lang;
          setIsRouting(false);
        }).catch((error) => {
          console.error('[LanguageRouter] Error changing language:', error);
          setIsRouting(false);
        });
      } else {
        document.documentElement.lang = lang;
        setIsRouting(false);
      }
    } else if (path !== '/') {
      // No language in path (except root), redirect to Turkish (default)
      const defaultLang = 'tr';
      const newPath = `/${defaultLang}${path}`;
      const willBeValid = isValidRoute(newPath);
      
      if (location.pathname !== newPath) {
        if (willBeValid) {
          setPendingNavigation({ path: newPath, lang: defaultLang });
        } else {
          setIsRouting(false);
        }
      } else {
        setIsRouting(false);
      }
    } else {
      // Root path, redirect to /tr (default language)
      const defaultLang = 'tr';
      const newPath = `/${defaultLang}`;
      
      if (location.pathname !== newPath) {
        setPendingNavigation({ 
          path: newPath, 
          lang: currentLang !== defaultLang ? defaultLang : null 
        });
      } else {
        if (currentLang !== defaultLang) {
          setIsRouting(true);
          i18n.changeLanguage(defaultLang).then(() => {
            document.documentElement.lang = defaultLang;
            setIsRouting(false);
          }).catch((error) => {
            console.error('[LanguageRouter] Error changing language:', error);
            setIsRouting(false);
          });
        } else {
          document.documentElement.lang = defaultLang;
          setIsRouting(false);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, navigate]);

  useEffect(() => {
    // Update HTML lang attribute
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return (
    <>
      <LoadTop />
      <WhatsAppButton />
      {/* Show loading overlay only when routing, but always render Routes */}
      {isRouting && (
        <div 
          className="preloader" 
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            zIndex: 9999
          }}
        >
          <div className="preloader-inner">
            <img src="/assets/img/logo.svg" alt="Haseki Sultan" style={{ maxWidth: '200px', animation: 'pulse 1.5s ease-in-out infinite' }} />
          </div>
          <style>{`
            @keyframes pulse {
              0%, 100% { opacity: 1; transform: scale(1); }
              50% { opacity: 0.7; transform: scale(0.95); }
            }
          `}</style>
        </div>
      )}
      <Routes>
        <Route path="/tr" element={<HomeOne />} />
        <Route path="/tr/" element={<HomeOne />} />
        <Route path="/en" element={<HomeOne />} />
        <Route path="/en/" element={<HomeOne />} />
        <Route path="/tr/home-tour" element={<HomeOne />} />
        <Route path="/en/home-tour" element={<HomeOne />} />
        <Route path="/tr/home-agency" element={<HomeOne />} />
        <Route path="/en/home-agency" element={<HomeOne />} />
        <Route path="/tr/home-yacht" element={<HomeOne />} />
        <Route path="/en/home-yacht" element={<HomeOne />} />
        <Route path="/tr/about" element={<About />} />
        <Route path="/en/about" element={<About />} />
        <Route path="/tr/destination" element={<Destination />} />
        <Route path="/en/destination" element={<Destination />} />
        <Route path="/tr/destination/:id" element={<DestinationDetails />} />
        <Route path="/en/destination/:id" element={<DestinationDetails />} />
        <Route path="/tr/service" element={<Service />} />
        <Route path="/en/service" element={<Service />} />
        <Route path="/tr/service/:id" element={<ServiceDetails />} />
        <Route path="/en/service/:id" element={<ServiceDetails />} />
        <Route path="/tr/activities" element={<Activities />} />
        <Route path="/en/activities" element={<Activities />} />
        <Route path="/tr/activities-details" element={<ActivitiesDetails />} />
        <Route path="/en/activities-details" element={<ActivitiesDetails />} />
        <Route path="/tr/shop" element={<Shop />} />
        <Route path="/en/shop" element={<Shop />} />
        <Route path="/tr/shop/:id" element={<ShopDetails />} />
        <Route path="/en/shop/:id" element={<ShopDetails />} />
        <Route path="/tr/cart" element={<Cart />} />
        <Route path="/en/cart" element={<Cart />} />
        <Route path="/tr/checkout" element={<Checkout />} />
        <Route path="/en/checkout" element={<Checkout />} />
        <Route path="/tr/wishlist" element={<Wishlist />} />
        <Route path="/en/wishlist" element={<Wishlist />} />
        <Route path="/tr/gallery" element={<Gallery />} />
        <Route path="/en/gallery" element={<Gallery />} />
        <Route path="/tr/tour" element={<Tour />} />
        <Route path="/en/tour" element={<Tour />} />
        <Route path="/tr/tour-details" element={<TourDetails />} />
        <Route path="/en/tour-details" element={<TourDetails />} />
        <Route path="/tr/resort" element={<Resort />} />
        <Route path="/en/resort" element={<Resort />} />
        <Route path="/tr/resort/:id" element={<ResortDetails />} />
        <Route path="/en/resort/:id" element={<ResortDetails />} />
        <Route path="/tr/tour-guide" element={<TourGuide />} />
        <Route path="/en/tour-guide" element={<TourGuide />} />
        <Route path="/tr/tour-guide/:id" element={<TourGuiderDetails />} />
        <Route path="/en/tour-guide/:id" element={<TourGuiderDetails />} />
        <Route path="/tr/faq" element={<Faq />} />
        <Route path="/en/faq" element={<Faq />} />
        <Route path="/tr/error" element={<Error />} />
        <Route path="/en/error" element={<Error />} />
        <Route path="/tr/blog" element={<Blog />} />
        <Route path="/en/blog" element={<Blog />} />
        <Route path="/tr/blog/:id" element={<BlogDetails />} />
        <Route path="/en/blog/:id" element={<BlogDetails />} />
        <Route path="/tr/contact" element={<Contact />} />
        <Route path="/en/contact" element={<Contact />} />
        {/* Catch-all route for 404 pages - use ErrorWrapper to prevent false 404s */}
        <Route path="*" element={<ErrorWrapper />} />
      </Routes>
    </>
  );
}

export default LanguageRouter;
